import { Skeleton, SkeletonRegion } from "@/components/ui/skeleton";
import { useCallback, useEffect } from "react";
import { X, CheckCircle2 } from "lucide-react";
import {
  useReconciliationState,
  useReconciliationStore,
} from "../store/useReconciliationStore";
import { useAppStore } from "../store/useAppStore";
import MismatchCard from "./MismatchCard";

const AUTO_CLOSE_MS = 1500;

export default function ReconciliationModal() {
  const { isOpen, mismatches, isLoading } = useReconciliationState();
  const close = useReconciliationStore((s) => s.close);
  const fetchMismatches = useReconciliationStore((s) => s.fetchMismatches);

  // Closing always refreshes the badge count so it reflects any resolutions
  // (an untouched dismissal simply re-reads the same pending count). A
  // resolution can edit or delete rows, so the month is reloaded too.
  const handleClose = useCallback(() => {
    close();
    const app = useAppStore.getState();
    app.loadPendingReconciliationCount();
    if (useReconciliationStore.getState().hasResolved) {
      app.loadMonth(app.viewYear, app.viewMonth);
    }
  }, [close]);

  useEffect(() => {
    if (isOpen) fetchMismatches();
  }, [isOpen, fetchMismatches]);

  // Once the fetch settles and no mismatches remain (last one resolved, or none
  // existed), show a success state and auto-close. open() seeds isLoading=true,
  // so this can't fire before the first fetch completes.
  const showSuccess = isOpen && !isLoading && mismatches.length === 0;
  useEffect(() => {
    if (!showSuccess) return;
    const timer = setTimeout(handleClose, AUTO_CLOSE_MS);
    return () => clearTimeout(timer);
  }, [showSuccess, handleClose]);

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm"
      onClick={(e) => {
        if (e.target === e.currentTarget) handleClose();
      }}
    >
      <div className="w-full max-w-lg mx-4 rounded-[16px] bg-card border-[0.5px] border-border shadow-xl p-6 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-5">
          <h2 className="text-[15px] font-semibold text-foreground">
            Reconcile transactions
          </h2>
          <button
            onClick={handleClose}
            aria-label="Close"
            className="p-1 rounded-md text-muted-foreground hover:text-foreground hover:bg-[var(--surface-muted)] transition-colors cursor-pointer"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {isLoading ? (
          <SkeletonRegion className="flex flex-col gap-3 py-2">
            <Skeleton className="h-36 rounded-xl" />
            <Skeleton className="h-36 rounded-xl" />
          </SkeletonRegion>
        ) : showSuccess ? (
          <div className="flex flex-col items-center gap-3 py-12 text-center">
            <CheckCircle2 className="w-8 h-8 text-emerald-500" />
            <span className="text-[14px] font-medium text-foreground">
              All mismatches resolved ✓
            </span>
          </div>
        ) : (
          <div className="flex flex-col gap-3">
            <p className="text-[12px] text-muted-foreground">
              Resolve each item below. For a conflict with a bank statement,
              choose the PDF version, keep yours, or edit manually. For a
              possible duplicate, confirm whether both rows are the same payment
              — the one you keep stays, the other is removed.
            </p>
            {mismatches.map((mismatch) => (
              <MismatchCard key={mismatch.id} mismatch={mismatch} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
